import React, { useState } from 'react';
import { Upload, FileText, Check, X, AlertCircle, Loader } from 'lucide-react';

export default function EvidenceCollection() {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([
    {
      id: 1,
      name: 'Environmental_Audit_Report_2024.pdf',
      size: '3.2 MB',
      status: 'validated',
      category: 'Environmental Permits',
      compliance: 'Air Quality Standards',
      uploadDate: '2024-12-01'
    },
    {
      id: 2,
      name: 'Safety_Inspection_Photos.zip',
      size: '15.8 MB',
      status: 'processing',
      category: 'Safety Certifications',
      compliance: 'Fire Safety',
      uploadDate: '2024-12-05'
    }
  ]);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      // Handle file upload
      console.log('Files dropped:', e?.dataTransfer?.files);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'validated':
        return <Check className="text-green-600" size={20} />;
      case 'processing':
        return <Loader className="text-blue-600 animate-spin" size={20} />;
      case 'failed':
        return <X className="text-red-600" size={20} />;
      default:
        return <AlertCircle className="text-gray-400" size={20} />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      validated: 'bg-green-100 text-green-800 border-green-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      failed: 'bg-red-100 text-red-800 border-red-200'
    };
    return styles?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Info Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Evidence Collection System</h3>
            <p className="text-sm text-blue-800">
              Upload structured documentation with automatic categorization, compliance mapping, and validation against regulatory requirements. 
              Supported formats: PDF, DOC, DOCX, JPG, PNG, ZIP (Max 50MB per file)
            </p>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50' :'border-gray-300 bg-gray-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Drop files here or click to upload
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Support for PDF, DOC, images, and compressed files
        </p>
        <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Browse Files
        </button>
      </div>

      {/* Upload Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Compliance Category
          </label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option>Environmental Permits</option>
            <option>Safety Certifications</option>
            <option>Operational Licenses</option>
            <option>Quality Standards</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Regulatory Requirement
          </label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option>Air Quality Standards</option>
            <option>Water Discharge Norms</option>
            <option>Fire Safety Requirements</option>
            <option>ISO Certification</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Document Type
          </label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option>Audit Report</option>
            <option>Inspection Record</option>
            <option>Test Results</option>
            <option>Photographic Evidence</option>
            <option>Supporting Documents</option>
          </select>
        </div>
      </div>

      {/* Uploaded Files List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Uploaded Evidence</h3>
          <span className="text-sm text-gray-600">{uploadedFiles?.length} files</span>
        </div>
        <div className="space-y-3">
          {uploadedFiles?.map((file) => (
            <div
              key={file?.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="text-blue-600" size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900 truncate">{file?.name}</h4>
                    {getStatusIcon(file?.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{file?.size}</span>
                    <span>•</span>
                    <span>{file?.category}</span>
                    <span>•</span>
                    <span>{file?.uploadDate}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded border ${getStatusBadge(file?.status)}`}>
                  {file?.status?.charAt(0)?.toUpperCase() + file?.status?.slice(1)}
                </span>
              </div>
              
              {file?.status === 'validated' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    <span className="text-green-700 font-medium">
                      Validated and mapped to: {file?.compliance}
                    </span>
                  </div>
                </div>
              )}
              
              {file?.status === 'processing' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full w-2/3 animate-pulse"></div>
                    </div>
                    <span className="text-sm text-gray-600">Processing...</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Validation Rules */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Automatic Validation Checks</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Check className="text-green-600" size={16} />
            <span className="text-gray-700">Document authenticity verification</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="text-green-600" size={16} />
            <span className="text-gray-700">Compliance requirement matching</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="text-green-600" size={16} />
            <span className="text-gray-700">Date validity checking</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="text-green-600" size={16} />
            <span className="text-gray-700">Metadata extraction</span>
          </div>
        </div>
      </div>
    </div>
  );
}